import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {LoadAllCountriesUseCase} from '51trips-frontend-core'
import {Dialog} from '../Dialog'
import {Button} from '../Button'
import {Input} from '../Input'
import {CountryPickerDialog} from './CountryPickerDialog.tsx'
import {getFlagUrl} from '../../utils/flagUrl.ts'
import {CountryRepository} from '../../repositories/CountryRepository.ts'
import {InvitationCodeItinerary, InvitationCodeRecord, InvitationCodeTraveler} from '../../domain/entities/InvitationCode.ts'
import {ItineraryAttachmentField} from './ItineraryAttachmentField.tsx'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconClose from '../../assets/icons/close.svg?react'

const loadAllCountriesUseCase = new LoadAllCountriesUseCase(new CountryRepository())

export type EditInvitationCodeMode = 'traveler' | 'trip'

export type EditInvitationCodeChanges = {
    traveler?: InvitationCodeTraveler
    itinerary?: InvitationCodeItinerary
}

interface EditInvitationCodeDialogProps {
    mode: EditInvitationCodeMode
    record: InvitationCodeRecord | null
    isSaving: boolean
    onSave: (changes: EditInvitationCodeChanges) => void
}

// Mirrors the design's "Datos del viajero" / "Datos del viaje" modals (one modal,
// content swapped by type — same pattern as the mockup's `modal.esViajero`/`esViaje`).
export const EditInvitationCodeDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>, EditInvitationCodeDialogProps>(
    ({mode, record, isSaving, onSave}, ref) => {
        const {t} = useTranslation('dashboard')
        const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
        const countryPickerDialog = useRef<React.ElementRef<typeof CountryPickerDialog>>(null)

        const [startDate, setStartDate] = useState('')
        const [endDate, setEndDate] = useState('')
        const [countryIds, setCountryIds] = useState<string[]>([])
        const [tripItineraryPdfUrl, setTripItineraryPdfUrl] = useState('')
        const [isUploadingItinerary, setIsUploadingItinerary] = useState(false)
        const [travelerName, setTravelerName] = useState('')
        const [travelerEmail, setTravelerEmail] = useState('')
        const [travelerPhone, setTravelerPhone] = useState('')

        const allCountries = useMemo(() => loadAllCountriesUseCase.loadAll(), [])
        const selectedCountries = useMemo(
            () => countryIds.map((id) => allCountries.find((c) => c.id === id)).filter((c): c is NonNullable<typeof c> => !!c),
            [countryIds, allCountries]
        )

        useEffect(() => {
            setStartDate(record?.itinerary?.startDate ? toDateInput(record.itinerary.startDate) : '')
            setEndDate(record?.itinerary?.endDate ? toDateInput(record.itinerary.endDate) : '')
            setCountryIds(record?.itinerary?.countryCodes ?? [])
            setTripItineraryPdfUrl(record?.itinerary?.tripItineraryPdfUrl ?? '')
            setTravelerName([record?.mainTraveler?.firstName, record?.mainTraveler?.lastName].filter(Boolean).join(' '))
            setTravelerEmail(record?.mainTraveler?.email ?? '')
            setTravelerPhone(record?.mainTraveler?.phone ?? '')
        }, [record, mode])

        useImperativeHandle(ref, () => ({
            open: () => dialog.current!.open(),
            close: dialog.current!.close
        }), [])

        const dateError = !!startDate && !!endDate && startDate > endDate
        const tripComplete = !!startDate && !!endDate && countryIds.length > 0 && !dateError
        const isValid = !record || (mode === 'trip' ? tripComplete && !isUploadingItinerary : true)

        const submit = () => {
            if (!record || !isValid) return

            if (mode === 'traveler') {
                const [firstName, ...rest] = travelerName.trim().split(/\s+/).filter(Boolean)
                onSave({
                    traveler: {
                        firstName: firstName || undefined,
                        lastName: rest.join(' ') || undefined,
                        email: travelerEmail.trim() || undefined,
                        phone: travelerPhone.trim() || undefined,
                    },
                })
            } else {
                onSave({
                    itinerary: {
                        name: selectedCountries.map((c) => c.title).join(' · '),
                        countryCodes: countryIds,
                        startDate: new Date(startDate).getTime(),
                        endDate: new Date(endDate).getTime(),
                        tripImageUrl: record.itinerary?.tripImageUrl,
                        tripItineraryPdfUrl: tripItineraryPdfUrl || undefined,
                    },
                })
            }
        }

        return (
            <Dialog
                ref={dialog}
                title={
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xl font-medium">{mode === 'traveler' ? t('dialogs.travelerData') : t('tripData')}</span>
                        <span className="text-[12.5px] text-neutralBlack-500">{record?.code}</span>
                    </div>
                }
                width="mid-wide"
                withHeaderDelimiter
                headerButtons={[
                    <Button key="complete" variant="primary" size="small" className="max-h-[32px]"
                            disabled={!isValid || isSaving || !record} onClick={submit}>
                        {isSaving ? <IconSpinner className="size-4"/> : t('dialogs.save')}
                    </Button>,
                    <Button key="close" variant="secondary" size="small" className="max-h-[32px]"
                            onClick={() => dialog.current!.close()}>
                        {t('cancel')}
                    </Button>,
                ]}
            >
                <div className="flex flex-col gap-4 max-w-[420px]">
                    {mode === 'traveler' ? (
                        <div className="flex flex-col gap-3">
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('dialogs.name')}</span>
                                <Input value={travelerName} onChange={(e) => setTravelerName(e.target.value)}
                                       placeholder={t('dialogs.fullNamePlaceholder')} className="h-10" block/>
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('dialogs.travelerPhoneLabel')}</span>
                                <Input type="tel" value={travelerPhone} onChange={(e) => setTravelerPhone(e.target.value)}
                                       placeholder={t('dialogs.travelerPhoneLabel')} className="h-10" block/>
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('email')}</span>
                                <Input value={travelerEmail} onChange={(e) => setTravelerEmail(e.target.value)}
                                       placeholder={t('dialogs.emailPlaceholder')} className="h-10" block/>
                            </label>
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2.5 flex-wrap">
                                <label className="flex-1 min-w-[130px] flex flex-col gap-1">
                                    <span className="text-xs text-neutralBlack-500">{t('dialogs.departureDate')}</span>
                                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                                           className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"/>
                                </label>
                                <label className="flex-1 min-w-[130px] flex flex-col gap-1">
                                    <span className="text-xs text-neutralBlack-500">{t('dialogs.returnDate')}</span>
                                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                                           className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"/>
                                </label>
                            </div>
                            {dateError && <span className="text-xs text-danger">{t('dialogs.dateOrderError')}</span>}

                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('dialogs.destination')}</span>
                                <button type="button" onClick={() => countryPickerDialog.current?.open()}
                                        className="h-10 border border-neutralBlack-200 rounded-lg text-[13px] text-neutralBlack-500 hover:border-neutralBlack-400 text-left px-3 bg-white">
                                    {countryIds.length ? t('dialogs.editDestinations') : t('dialogs.searchCountry')}
                                </button>
                                {selectedCountries.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {selectedCountries.map((c) => (
                                            <span key={c.id} className="inline-flex items-center gap-1.5 h-[30px] pl-2.5 pr-1.5 rounded-full bg-neutralBlack-900 text-white text-xs">
                                                <img src={getFlagUrl(c.id)} alt="" className="w-3.5 h-2.5 rounded-sm object-cover"/>
                                                {c.title}
                                                <button type="button" title={t('dialogs.removeChip')} onClick={() => setCountryIds((ids) => ids.filter((id) => id !== c.id))}
                                                        className="w-[18px] h-[18px] rounded-full bg-white/20 flex items-center justify-center">
                                                    <IconClose className="size-2"/>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </label>

                            <ItineraryAttachmentField
                                key={record?.id ?? 'new'}
                                value={tripItineraryPdfUrl}
                                onChange={(url) => setTripItineraryPdfUrl(url ?? '')}
                                onUploadingChange={setIsUploadingItinerary}
                            />

                            <span className="text-xs text-neutralBlack-400">
                                {t('packs.sidePanelTripHelp')}
                            </span>
                        </>
                    )}
                </div>

                <CountryPickerDialog
                    ref={countryPickerDialog}
                    selectedCountriesIDs={countryIds}
                    onSelectedCountriesIDsChanged={setCountryIds}
                />
            </Dialog>
        )
    }
)

function toDateInput(epochMs: number): string {
    return new Date(epochMs).toISOString().split('T')[0]
}
